/**
 * Serviço para autenticação e gerenciamento de usuários
 */
import {
  authApi,
  getJwt as _getJwt,
  setJwt,
  clearJwt,
  AUTH_LOGOUT_EVENT,
  formatErrorMessage,
} from './BaseApiService';
import { authEvents } from './AxiosConfig';

// Re-exportar getJwt para compatibilidade
export const getJwt = _getJwt;

/**
 * Realiza login com credenciais
 * @param {string} email - Email do usuário
 * @param {string} password - Senha do usuário
 * @returns {Promise<Object>} Resposta com token e dados do usuário
 */
export async function login(email, password) {
  try {
    const response = await authApi.post('/auth/login', { email, password });
    const token = response.data.token || response.data.access_token;

    if (token) {
      setJwt(token);

      // Salvar dados do usuário se disponíveis
      if (response.data.user) {
        try {
          sessionStorage.setItem('user', JSON.stringify(response.data.user));
        } catch (e) {
          console.warn('[AuthService] Erro ao salvar dados do usuário:', e);
        }
      }

      // Disparar evento de login bem-sucedido para notificar a aplicação
      window.dispatchEvent(authEvents.login);
      return response.data;
    } else {
      throw new Error('Token não encontrado na resposta');
    }
  } catch (error) {
    clearJwt();
    console.error('[AuthService] Erro no login:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha na autenticação');
  }
}

/**
 * Realiza logout do usuário atual.
 *
 * Best-effort: tenta invalidar o token no backend (`POST /auth/logout`,
 * adiciona à blacklist Redis tier=control via S12-10 Feature 2) antes de
 * limpar localmente. Falhas (rede caída, backend offline, 401) são
 * toleradas — `clearJwt()` SEMPRE roda. O backend trata logout como
 * idempotente (200 mesmo com token inválido/expirado).
 *
 * @returns {Promise<void>} Resolve após tentativa + clearJwt local.
 */
export async function logout() {
  const token = getJwt();
  if (token) {
    try {
      await authApi.post('/auth/logout', { token });
    } catch (error) {
      console.warn(
        '[AuthService] Falha ao invalidar token no backend (best-effort, ignorado):',
        error?.response?.status ?? error?.message ?? error,
      );
    }
  }
  clearJwt();
}

/**
 * Verifica se o usuário está autenticado
 * @returns {boolean} Verdadeiro se o usuário está autenticado
 */
export function isAuthenticated() {
  return !!getJwt();
}

/**
 * Registra um ouvinte para o evento de logout
 * @param {Function} callback - Função a ser chamada quando o logout acontecer
 * @returns {Function} Função para remover o ouvinte
 */
export function onLogout(callback) {
  const handler = () => callback();
  window.addEventListener(AUTH_LOGOUT_EVENT, handler);
  return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
}

/**
 * Obtém os dados do usuário autenticado
 * @returns {Promise<Object>} Dados do usuário
 */
export async function getUserData() {
  try {
    if (!isAuthenticated()) {
      throw new Error('Usuário não autenticado');
    }

    // Endpoint atualizado: /client/me
    const response = await authApi.get('/client/me');
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro ao obter dados do usuário:', error);

    if (error.response?.status === 401) {
      clearJwt();
    }

    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao obter dados do usuário');
  }
}

/**
 * Carrega o saldo do usuário
 * @returns {Promise<Object>} Dados do saldo
 */
export async function getUserBalance() {
  try {
    if (!isAuthenticated()) {
      throw new Error('Usuário não autenticado');
    }

    const response = await authApi.get('/client/balance');
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro ao obter saldo do usuário:', error);

    if (error.response?.status === 401) {
      clearJwt();
    }

    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao obter saldo do usuário');
  }
}

/**
 * Registra um novo usuário
 * @param {Object} userData - Dados do usuário para registro
 * @returns {Promise<Object>} Resposta da API
 */
export async function registerUser(userData) {
  try {
    const response = await authApi.post('/auth/register/individual', userData);
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro no registro de usuário:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha no registro de usuário');
  }
}

/**
 * Inicia o processo de recuperação de senha
 * @param {string} email - Email do usuário
 * @returns {Promise<Object>} Resposta da API
 */
export async function requestPasswordReset(email) {
  try {
    const response = await authApi.post('/api/v1/password/request-reset', { email });
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro na recuperação de senha:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao solicitar recuperação de senha');
  }
}

/**
 * Alias para requestPasswordReset para manter compatibilidade
 * @deprecated Use requestPasswordReset em vez disso
 */
export const forgotPassword = requestPasswordReset;

/**
 * Redefine a senha com um token de recuperação
 * @param {string} token - Token de recuperação
 * @param {string} newPassword - Nova senha
 * @returns {Promise<Object>} Resposta da API
 */
export async function resetPassword(token, newPassword) {
  try {
    const response = await authApi.post('/api/v1/password/reset', {
      token,
      new_password: newPassword,
    });
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro na redefinição de senha:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao redefinir senha');
  }
}

/**
 * Ativa a conta com um token de ativação
 * @param {string} token - Token de ativação
 * @returns {Promise<Object>} Resposta da API
 */
export async function activateAccount(token) {
  try {
    const response = await authApi.post('/api/v1/password/activate', { token });
    return response.data;
  } catch (error) {
    console.error('[AuthService] Erro na ativação da conta:', error);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error) || 'Falha ao ativar a conta');
  }
}

/**
 * Obtém os logs de atividade do usuário
 * @param {number} days - Número de dias para buscar (0 para todos os registros)
 * @returns {Promise<Array>} Lista de registros de atividade
 */
export async function getUserActivityLogs(_days = 7) {
  // G-10 (web-plataforma-adjustments Wave 2, 2026-05-26): gate-off.
  // Backend `gn-api-auth-service` NÃO possui endpoint de listagem de
  // atividades. Apenas `POST /api/v1/activity/log` (registrar) e
  // `POST /api/v1/activity/domain-activity`. Chamar `GET /api/v1/activity/log`
  // retornava 404/405 ruidoso em PROD. Aguardando issue de implementação
  // backend (GET /api/v1/activity?days=). Doc de referência:
  // architecture/to-be/01-compatibilizacao-pos-sprint12.md (D-105).
  if (!isAuthenticated()) {
    throw new Error('Usuário não autenticado');
  }
  if (import.meta.env.DEV) {
    console.warn(
      '[AuthService] getUserActivityLogs: backend ainda não expõe GET de atividades; retornando lista vazia.',
    );
  }
  return [];
}
